import { useCallback, useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { clearSession, getToken, subscribeToToken } from "../utils/auth";

const ACK_TIMEOUT_MS = 15000;

/** How long the tab may stay hidden before the socket is closed. */
const IDLE_DISCONNECT_MS = 60000;

/** Codes that mean retrying will not help until the user signs in again. */
const FATAL_AUTH_CODES = new Set(["NO_TOKEN", "TOKEN_EXPIRED", "INVALID_TOKEN"]);

/** Longest we wait for a handshake to settle before forcing the socket shut. */
const CLOSE_GRACE_MS = 10000;

/**
 * Closing a socket whose WebSocket is still handshaking makes the browser log
 * "WebSocket is closed before the connection is established". React StrictMode
 * hits this on every mount in development, so wait for the transport to open
 * (or fail) and close cleanly after that.
 */
function closeSocket(socket) {
  socket.removeAllListeners();

  if (socket.connected) {
    socket.disconnect();
    return;
  }

  const manager = socket.io;
  // This socket is being thrown away; don't let the manager keep retrying it.
  manager.reconnection(false);

  let timer = null;
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    if (timer) clearTimeout(timer);
    manager.off("open", finish);
    manager.off("error", finish);
    socket.disconnect();
  };

  manager.on("open", finish);
  manager.on("error", finish);
  timer = setTimeout(finish, CLOSE_GRACE_MS);
}

function redirectToLogin() {
  clearSession();
  if (
    typeof window !== "undefined" &&
    !window.location.pathname.includes("/login")
  ) {
    window.location.assign("/login");
  }
}

function socketBaseUrl() {
  let configured = process.env.REACT_APP_APIHUB_BASE_URL;
  if (configured) configured= configured.replace(/\/$/, "");
  else configured= "";
  console.log("socketBaseUrl",configured);
  return configured;
}

/**
 * Live chat inbox WebSocket. After the initial page load every read and write
 * goes through this socket: `request` emits an event and resolves with the
 * server's acknowledgement, so callers can await it like an HTTP call.
 */
export function useInboxSocket({
  phoneNoId,
  projectId,
  enabled,
  onMessageNew,
  onContactUpdated,
  onInboxCounts,
  idleDisconnectMs = IDLE_DISCONNECT_MS,
}) {
  const socketRef = useRef(null);
  const prevThreadRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [onlineAgents, setOnlineAgents] = useState([]);
  const [authError, setAuthError] = useState("");
  // Tracking the login token as state lets the socket reconnect when the user
  // signs in, signs out, or the token is refreshed in another tab.
  const [token, setTokenValue] = useState(() => getToken());

  useEffect(() => subscribeToToken(() => setTokenValue(getToken())), []);

  // Hold the connection open only while the inbox is actually being watched. A
  // tab hidden past the grace period drops it; coming back reopens and resyncs.
  const [idle, setIdle] = useState(false);

  useEffect(() => {
    if (!idleDisconnectMs || typeof document === "undefined") return undefined;

    let timer = null;
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        timer = setTimeout(() => setIdle(true), idleDisconnectMs);
      } else {
        if (timer) clearTimeout(timer);
        timer = null;
        setIdle(false);
      }
    };

    onVisibilityChange();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [idleDisconnectMs]);

  const request = useCallback(
    (event, payload = {}) =>
      new Promise((resolve, reject) => {
        const socket = socketRef.current;
        if (!socket?.connected) {
          reject(new Error("Live connection unavailable"));
          return;
        }
        let settled = false;
        const timer = setTimeout(() => {
          if (settled) return;
          settled = true;
          reject(new Error("Live request timed out"));
        }, ACK_TIMEOUT_MS);

        socket.emit(event, payload, (res) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          if (res && res.success === false) {
            reject(new Error(res.message || "Request failed"));
            return;
          }
          resolve(res || {});
        });
      }),
    [],
  );

  const joinThread = useCallback(
    async (customerId, options = {}) => {
      const socket = socketRef.current;
      if (!socket || !customerId) return [];
      if (prevThreadRef.current && prevThreadRef.current !== customerId) {
        socket.emit("thread:leave", { customerId: prevThreadRef.current });
      }
      prevThreadRef.current = customerId;
      const res = await request("thread:join", { customerId, ...options });
      return Array.isArray(res.messages) ? res.messages : [];
    },
    [request],
  );

  const leaveThread = useCallback(() => {
    const socket = socketRef.current;
    if (socket && prevThreadRef.current) {
      socket.emit("thread:leave", { customerId: prevThreadRef.current });
    }
    prevThreadRef.current = null;
  }, []);

  const fetchThread = useCallback(
    async (customerId, options = {}) => {
      const res = await request("thread:messages", { customerId, ...options });
      return Array.isArray(res.messages) ? res.messages : [];
    },
    [request],
  );

  const refreshContacts = useCallback(
    () => request("inbox:contacts", { phoneNoId }),
    [request, phoneNoId],
  );

  const sendMessage = useCallback(
    async (payload) => {
      const res = await request("message:send", payload);
      return res.data || null;
    },
    [request],
  );

  const changeStatus = useCallback(
    (payload) => request("chat:status", payload),
    [request],
  );

  useEffect(() => {
    if (!enabled || !phoneNoId || idle) return undefined;

    if (!token) {
      setAuthError("Sign in to use live chat");
      return undefined;
    }
    setAuthError("");

    const isProduction = process.env.NODE_ENV === "production";
    const socket = io(socketBaseUrl(), {
      path: "/socket.io",
      // Resolved per attempt, so a reconnect always sends the current token.
      auth: (cb) => cb({ token: getToken() || token }),
      extraHeaders: { Authorization: `Bearer ${token}` },
      withCredentials: true,
      // Production: HTTPS polling only (no wss). Dev: prefer websocket.
      transports: isProduction ? ["polling"] : ["websocket", "polling"],
      ...(isProduction ? { upgrade: false } : {}),
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);
      setAuthError("");
      socket.emit("inbox:join", { phoneNoId, projectId });
      if (prevThreadRef.current) {
        socket.emit("thread:join", { customerId: prevThreadRef.current });
      }
    });
    socket.on("disconnect", () => setConnected(false));

    socket.on("connect_error", (err) => {
      const code = err?.data?.code;
      setAuthError(err?.message || "Live connection failed");
      if (FATAL_AUTH_CODES.has(code)) {
        socket.disconnect();
        redirectToLogin();
      }
    });

    socket.on("auth:expired", () => {
      socket.disconnect();
      setConnected(false);
      redirectToLogin();
    });

    socket.on("message:new", (payload) => onMessageNew?.(payload));
    socket.on("contact:updated", (payload) => onContactUpdated?.(payload));
    socket.on("inbox:counts", (payload) => onInboxCounts?.(payload));
    socket.on("agents:snapshot", ({ agents }) => {
      setOnlineAgents(Array.isArray(agents) ? agents : []);
    });
    socket.on("agent:online", (agent) => {
      setOnlineAgents((prev) => [...prev.filter((a) => a.userId !== agent.userId), agent]);
    });
    socket.on("agent:offline", ({ userId }) => {
      setOnlineAgents((prev) => prev.filter((a) => a.userId !== userId));
    });

    const heartbeat = setInterval(() => {
      if (projectId) socket.emit("presence:heartbeat", { projectId });
    }, 30000);

    return () => {
      clearInterval(heartbeat);
      leaveThread();
      closeSocket(socket);
      socketRef.current = null;
      setConnected(false);
      setOnlineAgents([]);
    };
  }, [
    enabled,
    idle,
    token,
    phoneNoId,
    projectId,
    onMessageNew,
    onContactUpdated,
    onInboxCounts,
    leaveThread,
  ]);

  return {
    connected,
    idle,
    authError,
    onlineAgents,
    joinThread,
    leaveThread,
    fetchThread,
    refreshContacts,
    sendMessage,
    changeStatus,
  };
}
